import { CheckCircle, Sparkles, TrendingDown, TrendingUp, Zap } from 'lucide-react'
import type React from 'react'

import { Modal } from './Modal'

interface TradeSignalsModalProps {
    isOpen: boolean
    onClose: () => void
    signals: any[] // TradeSignal[];
    symbol: string
    onGenerateAiInsights: () => void
}

export const TradeSignalsModal: React.FC<TradeSignalsModalProps> = ({
    isOpen,
    onClose,
    signals,
    symbol,
    onGenerateAiInsights,
}) => {
    if (!isOpen) return null

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2">
                    <Zap className="w-6 h-6 text-yellow-500 fill-yellow-400" />
                    <span className="bg-linear-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent font-bold">
                        AI Trade Signals ({symbol})
                    </span>
                </div>
            }
            width="600px"
        >
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* AI CTAs */}
                <div className="bg-linear-to-r from-indigo-50 to-violet-50 p-4 rounded-xl border border-indigo-100 flex items-center justify-between shadow-sm">
                    <div>
                        <h4 className="text-sm font-bold text-indigo-900">Want deeper market analysis?</h4>
                        <p className="text-[10px] text-indigo-600 font-medium opacity-80">
                            Ask Gemini to analyze the complete Option Chain.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            onClose()
                            onGenerateAiInsights()
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg text-xs font-black uppercase tracking-wider shadow-sm border border-indigo-100 hover:scale-105 active:scale-95 transition-all hover:bg-white/80"
                    >
                        <Sparkles size={14} className="text-amber-500 fill-amber-500" />
                        <span>Generate Analysis</span>
                    </button>
                </div>

                {signals.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                        <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3 opacity-50" />
                        <p>No high-probability setups found right now.</p>
                        <p className="text-xs mt-2">Wait for price to reach Support/Resistance or Shift zones.</p>
                    </div>
                ) : (
                    signals.map((signal, idx) => (
                        <div
                            key={idx}
                            className={`relative overflow-hidden rounded-xl border p-4 shadow-lg transition-transform hover:scale-[1.02] ${
                                signal.type === 'LONG_CALL'
                                    ? 'border-emerald-500/30 bg-linear-to-br from-emerald-950/80 to-emerald-900/40 text-emerald-50 shadow-emerald-500/10'
                                    : 'border-rose-500/30 bg-linear-to-br from-rose-950/80 to-rose-900/40 text-rose-50 shadow-rose-500/10'
                            }`}
                        >
                            {/* Glass Effect Overlay */}
                            <div className="absolute inset-0 bg-white/5 pointer-events-none" />

                            <div className="relative flex justify-between items-start mb-3">
                                <div>
                                    <div
                                        className={`text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-1 ${
                                            signal.type === 'LONG_CALL'
                                                ? 'bg-emerald-500/20 text-emerald-300'
                                                : 'bg-rose-500/20 text-rose-300'
                                        }`}
                                    >
                                        {signal.type === 'LONG_CALL' ? 'BULLISH' : 'BEARISH'}
                                    </div>
                                    <h3 className="text-lg font-bold">
                                        Buy {signal.type === 'LONG_CALL' ? 'CE' : 'PE'}{' '}
                                        <span className="text-sm opacity-80">(Strike {signal.strike})</span>
                                    </h3>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-black tracking-tight premium-cell-mono">
                                        ₹{signal.optionLtp ? signal.optionLtp.toFixed(1) : '0.0'}
                                    </div>
                                    <div className="text-xs opacity-60">LTP</div>
                                </div>
                            </div>

                            <div className="relative grid grid-cols-3 gap-2 bg-black/20 rounded-lg p-3 mb-3 backdrop-blur-sm">
                                <div className="text-center">
                                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Entry</div>
                                    <div className="premium-cell-mono font-bold text-white">₹{signal.entryPrice}</div>
                                </div>
                                <div className="text-center border-l border-white/10">
                                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Target</div>
                                    <div className="premium-cell-mono font-bold text-emerald-400">
                                        ₹{signal.targetPrice}
                                    </div>
                                </div>
                                <div className="text-center border-l border-white/10">
                                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">
                                        Stop Loss
                                    </div>
                                    <div className="premium-cell-mono font-bold text-rose-400">₹{signal.stopLoss}</div>
                                </div>
                            </div>

                            <div className="relative text-xs opacity-80 leading-relaxed border-t border-white/10 pt-2 mt-2">
                                <span className="font-semibold text-yellow-200">Logic:</span> {signal.reason}
                            </div>

                            <div className="absolute top-0 right-0 p-2 opacity-10">
                                {signal.type === 'LONG_CALL' ? (
                                    <TrendingUp className="w-16 h-16 text-white" />
                                ) : (
                                    <TrendingDown className="w-16 h-16 text-white" />
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </Modal>
    )
}
